import { useState, useEffect } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, SERVICES } from "@/lib/api";

export default function Contact({ preselectedService }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({ ...f, service: preselectedService }));
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [preselectedService]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("Message received. We'll be in touch within 36 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-4">// GET_IN_TOUCH</div>
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-8">
            Let&apos;s have a<br />
            <span className="text-neon-orange neon-text-orange">conversation.</span>
          </h2>
          <p className="text-white/60 text-base md:text-lg mb-10 max-w-md">
            Tell us a little about what you&apos;re working on. We read every message personally and reply with something useful — not a template.
          </p>

          <div className="space-y-4 font-mono text-sm">
            <Line label="Response Time" val="Up to 36 hours" />
            <Line label="Introductory Call" val="Complimentary" />
            <Line label="Working With" val="US + Remote Globally" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="glass-strong p-8 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-neon-cyan to-transparent" />

            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 size={48} className="text-neon-lime mb-6" />
                <h3 className="font-heading font-bold text-2xl mb-3">Message Received</h3>
                <p className="text-white/60 max-w-sm">Thank you. We&apos;ll respond personally within 36 hours, depending on current workflow. In the meantime, TechBot in the corner can help with any quick questions.</p>
                <button onClick={() => setSent(false)} className="btn-ghost mt-8">Send Another</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} required testid="contact-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testid="contact-phone" />
                  <div>
                    <label className="block overline mb-2">// Venture *</label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={onChange}
                      required
                      data-testid="contact-service"
                      className="tsv-input appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#0F0F16]">Select a venture…</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.name} className="bg-[#0F0F16]">{s.name}</option>
                      ))}
                      <option value="General Inquiry" className="bg-[#0F0F16]">General Inquiry</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block overline mb-2">// Message *</label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    required
                    placeholder="Share a bit about your situation and what you're hoping to figure out…"
                    data-testid="contact-message"
                    className="tsv-input resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending…" : "Send Message"} <Send size={14} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block overline mb-2">
        // {label}{required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="tsv-input"
        autoComplete="off"
      />
    </div>
  );
}

function Line({ label, val }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-3">
      <span className="overline text-white/40">// {label}</span>
      <span className="text-white">{val}</span>
    </div>
  );
}
